import getApplicationEnvironment from "@/utils/getApplicationEnvironment";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import { OWN_ORIGIN } from "@/base/base.constants";

const { isProductionEnv, isStagingEnv, isDevEnv, isFeatureEnv } =
  getApplicationEnvironment();

const API_HOSTS = {
  DEV: "https://app.dev.newseladev.com",
  STAGING: "https://app.staging.newseladev.com",
  PROD: "https://app.newsela.com",
} as const;

/**
 * Phase II (completed): Migrated all backend API calls to use API_BASE_URL,
 * maintaining backward compatibility while enabling Phase III auto-detection.
 *
 * Phase III: Deployment Mode Auto-Detection
 *
 * Automatically detects deployment mode by comparing MONOLITH_ORIGIN with OWN_ORIGIN:
 *
 * - Full Deployment (MONOLITH_ORIGIN === OWN_ORIGIN):
 *   Both backend monolith and frontend are deployed to feature env.
 *   API calls → feature env, frontend nav → feature env
 *
 * - Lean Deployment (MONOLITH_ORIGIN !== OWN_ORIGIN):
 *   Only frontend is deployed to feature env, monolith reuses dev.
 *   API calls → dev, frontend nav → dev
 *
 * Ops controls deployment mode via MONOLITH_HOST env variable:
 * - Full: MONOLITH_HOST=https://featx.dev.newseladev.com (points to feature env)
 * - Lean: MONOLITH_HOST=https://app.dev.newseladev.com (points to dev)
 */
const USE_FEATURE_ENV_API = isFeatureEnv && MONOLITH_ORIGIN === OWN_ORIGIN;

export const API_BASE_URL = (() => {
  const isLocalHost = OWN_ORIGIN.includes("localhost");

  if (isLocalHost) {
    const shouldUseRemoteDevAPI =
      process.env["NEXT_PUBLIC_USE_REMOTE_DEV_API"] === "true";

    return shouldUseRemoteDevAPI ? API_HOSTS.DEV : MONOLITH_ORIGIN;
  }

  if (isProductionEnv) return API_HOSTS.PROD;
  if (isStagingEnv) return API_HOSTS.STAGING;
  if (isDevEnv) return API_HOSTS.DEV;
  if (isFeatureEnv) {
    return USE_FEATURE_ENV_API ? MONOLITH_ORIGIN : API_HOSTS.DEV;
  }
  return API_HOSTS.DEV;
})();

export const DEFAULT_API_TIMEOUT = 1_000 * 60 * 5;

export const DEFAULT_HEADERS = {
  "Content-Type": "application/json",
};

export const GLOBAL_REQUEST_INTERCEPTORS = [];

export const GLOBAL_RESPONSE_INTERCEPTORS = [];
